import { useTheme } from "@mui/joy/styles";
import { colors } from "./theme/tokens";
import { IconButton, Option, Select, Sheet, Tooltip, Typography } from "@mui/joy";
import NavItem from "./NavItem";
import { useLocation } from "react-router-dom";
import React from "react";
import { useRole } from "./Contexts/RoleContext";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ApartmentIcon from "@mui/icons-material/Apartment";
import EngineeringIcon from "@mui/icons-material/Engineering";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PeopleIcon from "@mui/icons-material/People";
import CampaignIcon from "@mui/icons-material/Campaign";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import PhoneIcon from "@mui/icons-material/Phone";
import SyncIcon from "@mui/icons-material/Sync";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const NAV_COLLAPSED_KEY = "nav-collapsed";
const NAV_WIDTH_EXPANDED = 220;
const NAV_WIDTH_COLLAPSED = 56;

export interface NavItemDisplay {
	Display: string;
	Route?: string;
	Icon?: React.ElementType;
	ChildItems?: Array<NavItemDisplay>;
}

export default function NavMenu() {
	const [loc, setLoc] = React.useState(useLocation());
	const location = useLocation();
	const { effectiveRole, emulatedRole, setEmulatedRole, isActuallyAdmin } = useRole();
	const [collapsed, setCollapsed] = React.useState(() => localStorage.getItem(NAV_COLLAPSED_KEY) === "true");

	React.useEffect(() => {
		setLoc(location);
	}, [location]);

	const toggleCollapsed = () => {
		setCollapsed((prev) => {
			const next = !prev;
			localStorage.setItem(NAV_COLLAPSED_KEY, String(next));
			return next;
		});
	};

	const getNavigation = () => {
		const all = [
			{
				Display: "Tickets",
				Icon: ConfirmationNumberIcon,
				ChildItems: [
					{ Display: "All Tickets", Route: "/alltickets" },
					{ Display: "Sales Tickets", Route: "/sales" },
				],
			},
			{ Display: "Buildings", Route: "/buildingportal", Icon: ApartmentIcon },
			{ Display: "Checked In Mechanics", Route: "/checkedin", Icon: EngineeringIcon },
			{
				Display: "Inspections",
				Icon: AssignmentIcon,
				ChildItems: [
					{ Display: "Annual", Route: "/1year" },
					{ Display: "5 Year", Route: "/5year" },
					{ Display: "Alterations", Route: "/alterations" },
				],
			},
			{ Display: "Follow Ups", Route: "/followups", Icon: EventNoteIcon },
			{
				Display: "Work Orders",
				Icon: ListAltIcon,
				ChildItems: [
					{ Display: "Pending", Route: "/uaplists" },
					{ Display: "Assigned", Route: "/aplists" },
					{ Display: "Finished", Route: "/finishedlists" },
				],
			},
			{
				Display: "Reports",
				Icon: AssessmentIcon,
				ChildItems: [
					{ Display: "Broken Elevators", Route: "/brokenelevators" },
					{ Display: "Out of Service Elevators", Route: "/outofserviceelevators" },
					{ Display: "Missing Annual Inspections", Route: "/missingannualinspections" },
				],
			},
			{ Display: "Users", Route: "/users", Icon: PeopleIcon },
			{ Display: "Elevator Ads", Route: "/elevatorads", Icon: CampaignIcon },
			{ Display: "Proposal Issues", Route: "/proposalsmissingbuilding", Icon: ReportProblemIcon },
		] as Array<NavItemDisplay>;

		const adminOnly = [
			{ Display: "RingCentral", Route: "/ringcentral", Icon: PhoneIcon },
			{ Display: "QBO Sync", Route: "/qbosync", Icon: SyncIcon },
			{ Display: "Automations", Route: "/automations", Icon: ScheduleIcon },
		] as Array<NavItemDisplay>;

		const user = [{ Display: "Buildings", Route: "/buildingportal", Icon: ApartmentIcon }] as Array<NavItemDisplay>;

		if (effectiveRole === "Admin" || effectiveRole === "Manager") {
			const nav = [...all];
			if (isActuallyAdmin()) nav.push(...adminOnly.filter((x) => !nav.some((n) => n.Route === x.Route)));
			return nav;
		}
		return user;
	};

	const theme = useTheme();
	const proposalIssuePaths = ["/proposalissues", "/proposalsmissingbuilding", "/proposalsmissingelevator"];
	const isSelected = (item: NavItemDisplay) =>
		item.Route === "/proposalsmissingbuilding"
			? proposalIssuePaths.includes(loc.pathname)
			: item.Route
				? loc.pathname === (item.Route as string)
				: (item.ChildItems?.some((child) => loc.pathname === (child.Route as string)) ?? false);

	return (
		<div
			style={{
				flexBasis: collapsed ? `${NAV_WIDTH_COLLAPSED}px` : `${NAV_WIDTH_EXPANDED}px`,
				width: collapsed ? NAV_WIDTH_COLLAPSED : NAV_WIDTH_EXPANDED,
				minWidth: collapsed ? NAV_WIDTH_COLLAPSED : NAV_WIDTH_EXPANDED,
				height: "100%",
				backgroundColor: theme.palette.background.nav,
				display: "flex",
				flexDirection: "column",
				transition: "width 0.2s ease, flex-basis 0.2s ease, min-width 0.2s ease",
				overflow: "hidden",
			}}>
			<Sheet
				sx={{
					px: collapsed ? 0.5 : 1.5,
					py: 0.75,
					textAlign: "center",
					backgroundColor: theme.palette.background.nav,
					display: "flex",
					alignItems: "center",
					justifyContent: collapsed ? "center" : "space-between",
					gap: 0.5,
				}}>
				{collapsed ? (
					<Tooltip title="Dynamic Elevator Control Corp." placement="right" arrow>
						<Typography level="body-sm" fontWeight={700} sx={{ color: colors.brand.accent, cursor: "default" }}>
							D
						</Typography>
					</Tooltip>
				) : (
					<Typography level="body-sm" fontWeight={600} sx={{ color: theme.palette.text.nav, flex: 1 }}>
						<span style={{ color: colors.brand.accent }}>Dynamic</span> Elevator Control Corp.
					</Typography>
				)}
				<Tooltip title={collapsed ? "Expand navigation" : "Collapse navigation"} placement="right" arrow>
					<IconButton
						size="sm"
						variant="plain"
						onClick={toggleCollapsed}
						sx={{ color: colors.text.navMuted, "--IconButton-size": "28px", minWidth: 28, minHeight: 28 }}>
						{collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
					</IconButton>
				</Tooltip>
			</Sheet>
			<div
				style={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
				}}>
				{getNavigation().map((x) => (
					<NavItem key={x.Display} item={x} selected={isSelected(x)} collapsed={collapsed} currentPath={loc.pathname} />
				))}
			</div>
			<div style={{ flexShrink: 0, borderTop: `1px solid ${colors.neutral[800]}` }}>
				{isActuallyAdmin() && !collapsed && (
					<Select
						size="sm"
						value={emulatedRole ?? ""}
						placeholder="Emulate role..."
						onChange={(_, value) => setEmulatedRole(value === "" ? null : value)}
						color={emulatedRole ? "warning" : "neutral"}
						variant="soft"
						sx={{ mx: 0.75, my: 0.5, fontSize: "0.75rem" }}>
						<Option value="">My Role (Admin)</Option>
						<Option value="Manager">Emulate: Manager</Option>
						<Option value="Client">Emulate: Client</Option>
						<Option value="Ticketing">Emulate: Ticketing</Option>
					</Select>
				)}
				<NavItem
					item={{ Display: "Logout", Route: "/logout", Icon: LogoutIcon }}
					selected={false}
					collapsed={collapsed}
					currentPath={loc.pathname}
				/>
			</div>
		</div>
	);
}
